import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import {
  IProjectDesignModification,
  EDesignModificationConfirmationActionType,
  EDesignModificationStatus,
  EDesignModificationIssueRole,
  EDesignModificationFormKeys,
  ICreateProjectDesignModificationRequest,
  EDesignModificationFormSubmitType,
} from '@designModification/designModification.models';
import {
  Button,
  ComponentType,
  convertDateToStringIgnoringTimezone,
  convertISOStringToLocalDateString,
  FormFieldMethods,
  Icon,
  IOption,
  ToastMessage,
  toastMessage,
  UseFormReturn,
} from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import './DesignModificationDetailPage.scss';
import { useNavigate } from '@routes/routes.hooks';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import {
  useApproveDesignModification,
  useCancelDesignModification,
  useCreateDesignModification,
  useCreateDesignModificationRevision,
  useRequestEndorsementDesignModification,
  useUpdateDesignModification,
  useApproveAndCheckedAsLambethDesignModification,
} from '@designModification/designModification.queries';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { FETCH_QUERIES_NAME } from '@common/constants';
import {
  useIsAttachmentUploadLocked,
  useIsDesignerAttachmentUploadEnabled,
  useT3TopBtnsVisibility,
} from '@designModification/designModification.hooks';
import { IReasonOfChangeBase } from '@reasonOfChange/reasonOfChange.models';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { INoteItem, NoteList } from '@core/Note/NoteList';
import { useMemo } from 'react';
import { ERiskCategory } from '@riskCategory/riskCategory.constants';
import { IProjectDrawingRegisterSearch } from '@drawing/projectDrawingRegister.models';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { IProjectTeamOption } from '@projectTeam/projectTeam.models';
import { DownloadButton } from '@file/DownloadButton';
import {
  downloadDesignModificationPdfAsync,
  exportDesignModificationRfcAsync,
} from '@designModification/designModification.services';
import { useDeferLoading } from '@common/loading.hooks';
import { DesignModificationDetailTabForm } from './DesignModificationDetailTabForm';
import { useDesignModificationConfirmationModal } from './designModificationConfirmationModal.hooks';
import { DesignModificationDetailTabSignature } from './DesignModificationDetailTabSignature';
import { useDesignModificationReassignCheckerModal } from './designModificationReassignCheckerModal.hooks';

export interface IDesignModificationDetailTabProps {
  id?: number;
  masterScheduleId?: number;
  masterSchedule?: IMasterScheduleDetail;
  projectId: number;
  designModification?: IProjectDesignModification;
  isReadOnly: boolean;
  mode: DetailPageAccessMode;
  searchParamIssueRole?: EDesignModificationIssueRole;
  isSuperseded: boolean;
  useFormMethods: UseFormReturn;
  formFieldMethods: FormFieldMethods;
  checkerCompanyOptions?: IProjectCompanyOption[];
  checkerTeamOptions?: IProjectTeamOption[];
  designerCompanyOptions?: IProjectCompanyOption[];
  designerTeamOptions?: IProjectTeamOption[];
}

export const DesignModificationDetailTab: React.FunctionComponent<IDesignModificationDetailTabProps> = ({
  id,
  masterScheduleId,
  projectId,
  designModification,
  isReadOnly,
  mode,
  masterSchedule,
  searchParamIssueRole,
  isSuperseded,
  useFormMethods,
  formFieldMethods,
  checkerCompanyOptions,
  checkerTeamOptions,
  designerCompanyOptions,
  designerTeamOptions,
}) => {
  const queryClient = useQueryClient();

  const { navigate } = useNavigate();

  const formDataRiskCategory = useFormMethods.watch(EDesignModificationFormKeys.RISK_CATEGORY)?.category as
    | ERiskCategory
    | undefined;

  const topBtnsVisibility = useT3TopBtnsVisibility(
    id ?? 0,
    isSuperseded,
    designModification,
    searchParamIssueRole,
    formDataRiskCategory,
    masterSchedule,
  );

  const isAttachmentUploadLocked = useIsAttachmentUploadLocked(masterSchedule);
  const isDesignerAttachmentUploadEnabled = useIsDesignerAttachmentUploadEnabled(isSuperseded, designModification);

  const [confirmationActionType, setConfirmationActionType] =
    React.useState<EDesignModificationConfirmationActionType>();

  const { renderModal: renderConfirmationPortal, openPortal: openConfirmationPortal } =
    useDesignModificationConfirmationModal({
      confirmationActionType,
      onConfirm: () => {
        switch (confirmationActionType) {
          case EDesignModificationConfirmationActionType.create:
            useFormMethods.handleSubmit(onSubmit(EDesignModificationFormSubmitType.create))();
            break;
          case EDesignModificationConfirmationActionType.update:
            useFormMethods.handleSubmit(onSubmit(EDesignModificationFormSubmitType.update))();
            break;
          case EDesignModificationConfirmationActionType.issueToTwcAsSrp:
            useFormMethods.handleSubmit(
              onSubmit(
                !id ? EDesignModificationFormSubmitType.create : EDesignModificationFormSubmitType.approve,
                EDesignModificationIssueRole.srp,
              ),
            )();
            break;
          case EDesignModificationConfirmationActionType.issueToTwcAsDesigner:
            useFormMethods.handleSubmit(
              onSubmit(
                !id ? EDesignModificationFormSubmitType.create : EDesignModificationFormSubmitType.approve,
                EDesignModificationIssueRole.designer,
              ),
            )();
            break;
          case EDesignModificationConfirmationActionType.issueToDesigner:
            useFormMethods.handleSubmit(
              onSubmit(
                !id ? EDesignModificationFormSubmitType.create : EDesignModificationFormSubmitType.approve,
                EDesignModificationIssueRole.twc,
              ),
            )();
            break;
          case EDesignModificationConfirmationActionType.approveDesigner:
            if (!id)
              useFormMethods.handleSubmit(onSubmit(EDesignModificationFormSubmitType.createAndIssueAsDesigner))();
            else useFormMethods.handleSubmit(onSubmit(EDesignModificationFormSubmitType.approve))();
            break;
          case EDesignModificationConfirmationActionType.approveReviewer:
          case EDesignModificationConfirmationActionType.issueToChecker:
          case EDesignModificationConfirmationActionType.releaseForConstruction:
          case EDesignModificationConfirmationActionType.approveChecker:
            if (id) useFormMethods.handleSubmit(onSubmit(EDesignModificationFormSubmitType.approve))();
            break;
          case EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth:
            if (id)
              useFormMethods.handleSubmit(onSubmit(EDesignModificationFormSubmitType.approveAndCheckedAsLambeth))();
            break;
          case EDesignModificationConfirmationActionType.requestEndorsement:
            if (id) mutateRequestEndorsement({ projectId, id });
            break;
          case EDesignModificationConfirmationActionType.cancel:
            if (id) mutateCancel({ projectId, id });
            break;
          case EDesignModificationConfirmationActionType.createRevision:
            if (id) mutateCreateRevision({ id, projectId });
            break;
          default:
            break;
        }
      },
    });

  const { openPortal: openReassignCheckerModal, renderModal: renderReassignCheckerModal } =
    useDesignModificationReassignCheckerModal({});

  const mutationCreate = useCreateDesignModification();

  const {
    mutate: mutateCreate,
    isLoading: isLoadingCreate,
    isError: isErrorCreate,
    data: createResult,
    isSuccess: isSuccessCreate,
    error: errorCreate,
    reset: resetCreate,
  } = mutationCreate;

  const {
    mutate: mutateCreateRevision,
    isLoading: isLoadingCreateRevision,
    isSuccess: isSuccessCreateRevision,
    isError: isErrorCreateRevision,
    error: errorCreateRevision,
    reset: resetCreateRevision,
    data: dataCreateRevision,
  } = useCreateDesignModificationRevision();

  const {
    mutate: mutateUpdate,
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
    data: dataUpdate,
    isSuccess: isSuccessUpdate,
    error: errorUpdate,
    reset: resetUpdate,
  } = useUpdateDesignModification();

  const mutationApprove = useApproveDesignModification();
  const {
    mutate: mutateApprove,
    isLoading: isLoadingApprove,
    isError: isErrorApprove,
    isSuccess: isSuccessApprove,
    error: errorApprove,
    reset: resetApprove,
  } = mutationApprove;

  const mutationApproveAndCheckedAsLambeth = useApproveAndCheckedAsLambethDesignModification();
  const {
    mutate: mutateApproveAndCheckedAsLambeth,
    isError: isErrorApproveAndCheckedAsLambeth,
    isLoading: isLoadingApproveAndCheckedAsLambeth,
    isSuccess: isSuccessApproveAndCheckedAsLambeth,
    error: errorApproveAndCheckedAsLambeth,
    reset: resetApproveAndCheckedAsLambeth,
  } = mutationApproveAndCheckedAsLambeth;

  const {
    mutate: mutateRequestEndorsement,
    isLoading: isLoadingRequestEndorsement,
    isError: isErrorRequestEndorsement,
    data: dataRequestEndorsement,
    isSuccess: isSuccessRequestEndorsement,
    error: errorRequestEndorsement,
    reset: resetRequestEndorsement,
  } = useRequestEndorsementDesignModification();

  const {
    mutate: mutateCancel,
    isLoading: isLoadingCancel,
    isError: isErrorCancel,
    data: dataCancel,
    isSuccess: isSuccessCancel,
    error: errorCancel,
    reset: resetCancel,
  } = useCancelDesignModification();

  const { loading: isAnyActionLoading, setLoading: setIsAnyActionLoading } = useDeferLoading(
    isLoadingCreate ||
      isLoadingApproveAndCheckedAsLambeth ||
      isLoadingUpdate ||
      isLoadingApprove ||
      isLoadingRequestEndorsement ||
      isLoadingCancel,
  );

  const openConfirmationModalWithType = (
    confirmType: EDesignModificationConfirmationActionType,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setConfirmationActionType(confirmType);
    openConfirmationPortal(e);
  };

  const onSubmit = React.useCallback(
    (type: EDesignModificationFormSubmitType, issueRole?: EDesignModificationIssueRole) => (data: any) => {
      const {
        riskCategory,
        changeReasons,
        drawingsModified,
        designerAttachments,
        designerAttachmentsCalculation,
        requiredApprovalDate,
        ...rest
      } = data;

      if (
        (type === EDesignModificationFormSubmitType.approve ||
          type === EDesignModificationFormSubmitType.approveAndCheckedAsLambeth) &&
        !(isAttachmentUploadLocked || !isDesignerAttachmentUploadEnabled) &&
        (data[EDesignModificationFormKeys.DESIGNER_ATTACHMENTS] ?? []).length <= 0 &&
        (data[EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION] ?? []).length <= 0
      ) {
        toastMessage(
          <ToastMessage
            message="T3 form may not be issued without designer attachment or designer supporting attachment attached. Please ensure that attachments are available before raising T3 forms."
            toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
          />,
        );
        return;
      }

      const selectedChangeReasons = (changeReasons ?? []) as IReasonOfChangeBase[];
      const originalChangeReasons = designModification?.changeReasons ?? [];
      const changeReasonsData = selectedChangeReasons.map((r) => {
        const found = originalChangeReasons.find((ocr) => {
          return ocr.reasonOfChangeId === r.id;
        });
        return { ...found, reasonOfChangeId: r.id, projectDesignModificationId: id };
      });

      const drawingsModifiedRemovedDuplicated = (
        (drawingsModified ?? []) as IOption<IProjectDrawingRegisterSearch>[]
      ).filter((option, index, self) => index === self.findIndex((s) => s.key === option.key));
      const dataDrawingModified = drawingsModifiedRemovedDuplicated.map((d) => {
        const found = designModification?.drawingsModified?.find((originalDrawing) => {
          return originalDrawing.projectDrawingRegisterId === d.data?.id;
        });
        if (found) return found;

        return {
          projectDesignModificationId: id,
          projectDrawingRegisterId: d.data?.id,
          projectDrawingRegisterHistoryId: d.data?.latestRfcHistory?.id,
        };
      });

      const request = {
        projectId,
        masterScheduleId,
        riskCategory: riskCategory?.category,
        discissions: [],
        changeReasons: changeReasonsData,
        drawingsModified: dataDrawingModified,
        [EDesignModificationFormKeys.REQUIRED_APPROVAL_DATE]: convertDateToStringIgnoringTimezone(
          new Date(requiredApprovalDate),
        ),
        ...rest,
      };

      if (type === EDesignModificationFormSubmitType.create) {
        mutateCreate({ request: request as ICreateProjectDesignModificationRequest, issueRole });
        return;
      }

      const updateRequest = {
        ...request,
        id,
        rowVersion: designModification?.rowVersion,
        designChangeNo: designModification?.designChangeNo,
      };

      if (type === EDesignModificationFormSubmitType.update) {
        mutateUpdate(updateRequest);
        return;
      }
      if (type === EDesignModificationFormSubmitType.approve) {
        mutateApprove({
          request: { ...updateRequest, isRfc: (designModification?.rfcOn?.length ?? 0) > 0 },
          issueRole,
        });
        return;
      }
      if (type === EDesignModificationFormSubmitType.approveAndCheckedAsLambeth)
        mutateApproveAndCheckedAsLambeth(updateRequest);
    },
    [mode, isAttachmentUploadLocked, isDesignerAttachmentUploadEnabled, JSON.stringify(designModification)],
  );

  const notes: INoteItem<React.ReactElement<typeof Icon>>[] = React.useMemo(() => {
    const tempNotes: INoteItem<React.ReactElement<typeof Icon>>[] = [];

    if (!designModification) return tempNotes;

    switch (designModification.status) {
      case EDesignModificationStatus.cancel:
        tempNotes.push({
          componentType: ComponentType.Primary,
          prefixIcon: 'ExclamationCircle',
          note: `Cancelled by ${designModification.cancelledName}`.concat(
            designModification.cancelledDate
              ? ` on ${convertISOStringToLocalDateString(designModification.cancelledDate)}`
              : '',
          ),
        });
        break;
      case EDesignModificationStatus.referToDesignerTransmittal:
        if (designModification.relatedDesignerTransmittal)
          tempNotes.push({
            componentType: ComponentType.Primary,
            prefixIcon: 'ExclamationCircle',
            note: (
              <>
                Refer to transmittal:{' '}
                <span
                  className="gcl-fl-md"
                  onClick={() => {
                    navigate(routes.transmittals.designerTransmittal.item.path, {
                      id: designModification.relatedDesignerTransmittal?.id,
                    });
                  }}
                >
                  {designModification.relatedDesignerTransmittal.transmittalNo}
                </span>{' '}
                for changes.
              </>
            ),
          });
        break;

      default:
        break;
    }

    if (designModification.rfcProjectUserProfile) {
      tempNotes.push({
        componentType: ComponentType.Primary,
        prefixIcon: 'ExclamationCircle',
        note: `Released for Construction by ${
          designModification.rfcProjectUserProfile.userProfile?.displayName
        } on ${convertISOStringToLocalDateString(designModification.rfcOn ?? '')}`,
      });
    }

    return tempNotes;
  }, [JSON.stringify(designModification)]);

  const leftButtonBarItems = useMemo(() => {
    if (id && !designModification) return [];
    if (designModification?.status === EDesignModificationStatus.cancel) return [];

    const btnDownloadRfcT3WithCalReport = (
      <DownloadButton
        key="printRfcWithCalReport"
        exportFunction={exportDesignModificationRfcAsync}
        defaultExportProps={{
          projectId,
          id: designModification?.id as number,
          withCalReport: true,
        }}
        buttonRender={(exportFile) => (
          <Button prefixIconName="ArrowBarDown" type={ComponentType.Secondary} onClick={() => exportFile()}>
            Download RFC T3
          </Button>
        )}
      />
    );
    const btnDownloadRfcT3WithoutCalReport = (
      <DownloadButton
        key="printRfcWithoutCalReport"
        exportFunction={exportDesignModificationRfcAsync}
        defaultExportProps={{
          projectId,
          id: designModification?.id as number,
          withCalReport: false,
        }}
        buttonRender={(exportFile) => (
          <Button prefixIconName="ArrowBarDown" type={ComponentType.Secondary} onClick={() => exportFile()}>
            Download RFC T3 without Cal/Report
          </Button>
        )}
      />
    );

    if (designModification?.status === EDesignModificationStatus.completed) {
      return [btnDownloadRfcT3WithCalReport, btnDownloadRfcT3WithoutCalReport];
    }

    const btnPrint = (
      <DownloadButton
        key="printPdf"
        exportFunction={downloadDesignModificationPdfAsync}
        defaultExportProps={{
          projectId,
          id: designModification?.id as number,
        }}
        buttonRender={(exportFile) => (
          <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
            Print
          </Button>
        )}
      />
    );

    const topBtns: IDetailPageTopBarItem[] = [];

    if (
      designModification?.status === EDesignModificationStatus.revised ||
      designModification?.status === EDesignModificationStatus.referToDesignerTransmittal
    )
      return [btnPrint];

    if (topBtnsVisibility.isShowSaveBtn)
      topBtns.push({
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Primary,
        isError: mode === 'Create' ? isErrorCreate && confirmationActionType === undefined : isErrorUpdate,
        isLoading: mode === 'Create' ? isLoadingCreate && confirmationActionType === undefined : isLoadingUpdate,
        isSuccess: mode === 'Create' ? isSuccessCreate && confirmationActionType === undefined : isSuccessUpdate,
        title: 'Save',
        buttonIconName: 'FolderCheck',
        onClick: (e: any) => {
          setConfirmationActionType(undefined);
          useFormMethods.handleSubmit(
            onSubmit(
              mode === 'Create' ? EDesignModificationFormSubmitType.create : EDesignModificationFormSubmitType.update,
            ),
          )();
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowIssueToTwcAsSrpBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError:
          (isErrorApprove || isErrorCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsSrp,
        isLoading:
          (isLoadingApprove || isLoadingCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsSrp,
        isSuccess:
          (isSuccessApprove || isSuccessCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsSrp,
        title: 'Issue to TWC as SRP',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.issueToTwcAsSrp, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowIssueToTwcAsDesignerBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError:
          (isErrorApprove || isErrorCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsDesigner,
        isLoading:
          (isLoadingApprove || isLoadingCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsDesigner,
        isSuccess:
          (isSuccessApprove || isSuccessCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsDesigner,
        title: 'Issue to TWC as Designer',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.issueToTwcAsDesigner, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowIssueToDesignerBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError:
          (isErrorApprove || isErrorCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToDesigner,
        isLoading:
          (isLoadingApprove || isLoadingCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToDesigner,
        isSuccess:
          (isSuccessApprove || isSuccessCreate) &&
          confirmationActionType === EDesignModificationConfirmationActionType.issueToDesigner,
        title: 'Issue to Designer as TWC',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.issueToDesigner, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowDesignerApproveBtn)
      topBtns.push({
        buttonIconName: 'Pen',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError: isErrorApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveDesigner,
        isLoading:
          isLoadingApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveDesigner,
        isSuccess:
          isSuccessApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveDesigner,
        title: 'Approve as Designer',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.approveDesigner, e);
        },
        disable: isAnyActionLoading,
      });
    if (topBtnsVisibility.isShowDesignAgreedAndCheckedByLambethBtn) {
      topBtns.push({
        buttonIconName: 'Pen',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError:
          isErrorApproveAndCheckedAsLambeth &&
          confirmationActionType === EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth,
        isLoading:
          isLoadingApproveAndCheckedAsLambeth &&
          confirmationActionType === EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth,
        isSuccess:
          isSuccessApproveAndCheckedAsLambeth &&
          confirmationActionType === EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth,
        title: 'Design Agreed and Checked By Lambeth',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth, e);
        },
        disable: isAnyActionLoading,
      });
    }

    if (topBtnsVisibility.isShowReviewerApproveBtn)
      topBtns.push({
        buttonIconName: 'Pen',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError: isErrorApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveReviewer,
        isLoading:
          isLoadingApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveReviewer,
        isSuccess:
          isSuccessApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveReviewer,
        title: 'Approve as Reviewer',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.approveReviewer, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowIssueToCheckerBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError: isErrorApprove && confirmationActionType === EDesignModificationConfirmationActionType.issueToChecker,
        isLoading:
          isLoadingApprove && confirmationActionType === EDesignModificationConfirmationActionType.issueToChecker,
        isSuccess:
          isSuccessApprove && confirmationActionType === EDesignModificationConfirmationActionType.issueToChecker,
        title: 'Issue to Checker',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.issueToChecker, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowReleaseForConstructionBtn)
      topBtns.push({
        buttonIconName: 'Building',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Warning,
        isError:
          isErrorApprove && confirmationActionType === EDesignModificationConfirmationActionType.releaseForConstruction,
        isLoading:
          isLoadingApprove &&
          confirmationActionType === EDesignModificationConfirmationActionType.releaseForConstruction,
        isSuccess:
          isSuccessApprove &&
          confirmationActionType === EDesignModificationConfirmationActionType.releaseForConstruction,
        title: 'Release for construction',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.releaseForConstruction, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowIceApproveBtn)
      topBtns.push({
        buttonIconName: 'Pen',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError: isErrorApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveChecker,
        isLoading:
          isLoadingApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveChecker,
        isSuccess:
          isSuccessApprove && confirmationActionType === EDesignModificationConfirmationActionType.approveChecker,
        title: 'Approve as ICE',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.approveChecker, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowRequestEndorsementBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Secondary,
        isError: isErrorRequestEndorsement,
        isLoading: isLoadingRequestEndorsement,
        isSuccess: isSuccessRequestEndorsement,
        title: 'Request Endorsement',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.requestEndorsement, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowReassignICEBtn)
      topBtns.push({
        buttonIconName: 'EnvelopeCheck',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Danger,
        isError: false,
        isLoading: false,
        isSuccess: false,
        title: 'Re-assign Checker',
        onClick: (e: any) => {
          openReassignCheckerModal(e);
          // openConfirmationModalWithType(EDesignModificationConfirmationActionType.reassignChecker, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowCreateRevisionBtn)
      topBtns.push({
        buttonIconName: 'Stickies',
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Info,
        isError: isErrorCreateRevision,
        isLoading: isLoadingCreateRevision,
        isSuccess: isSuccessCreateRevision,
        title: 'Create Revision',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.createRevision, e);
        },
        disable: isAnyActionLoading,
      });

    if (topBtnsVisibility.isShowCancelBtn)
      topBtns.push({
        buttonIconName: 'FileExcel',
        title: 'Cancel',
        onClick: (e: any) => {
          openConfirmationModalWithType(EDesignModificationConfirmationActionType.cancel, e);
        },
        isLoading: isLoadingCancel,
        isError: isErrorCancel,
        isSuccess: isSuccessCancel,
        componentType: ComponentType.Danger,
        type: EDetailPageTopBarItemType.loadingButton,
        disable: isAnyActionLoading,
      });

    return topBtns;
  }, [
    isAnyActionLoading,
    JSON.stringify(designModification),
    isErrorCreate,
    isLoadingCreate,
    isSuccessCreate,
    isLoadingUpdate,
    isErrorUpdate,
    isSuccessUpdate,
    isLoadingApprove,
    isErrorApprove,
    isSuccessApprove,
    isLoadingApproveAndCheckedAsLambeth,
    isErrorApproveAndCheckedAsLambeth,
    isSuccessApproveAndCheckedAsLambeth,
    isLoadingRequestEndorsement,
    isSuccessRequestEndorsement,
    isErrorRequestEndorsement,
    isLoadingCancel,
    isSuccessCancel,
    isErrorCancel,
    isErrorCreateRevision,
    isSuccessCreateRevision,
    isLoadingCreateRevision,
    confirmationActionType,
    JSON.stringify(topBtnsVisibility),
    mode,
    isSuperseded,
  ]);

  return (
    <div>
      <ApiSuccessToast
        successes={[
          { isSuccess: isSuccessCreate, message: 'Issue Design Modification Success', reset: resetCreate },
          { isSuccess: isSuccessUpdate, message: 'Update Design Modification Success', reset: resetUpdate },
          {
            isSuccess: isSuccessApprove,
            message: 'Approve Design Modification Success',
            reset: resetApprove,
          },
          {
            isSuccess: isSuccessApproveAndCheckedAsLambeth,
            message: 'Approve Design Modification Success',
            reset: resetApproveAndCheckedAsLambeth,
          },
          {
            isSuccess: isSuccessRequestEndorsement,
            message: 'Request Endorsement Success',
            reset: resetRequestEndorsement,
          },
          {
            isSuccess: isSuccessCancel,
            message: 'Cancel Design Modification Success',
            reset: resetCancel,
          },
          {
            isSuccess: isSuccessCreateRevision,
            message: 'Create Design Modification revision success.',
            reset: resetCreateRevision,
          },
        ]}
        callbackAfterToasted={() => {
          queryClient.invalidateQueries([projectId, id, FETCH_QUERIES_NAME.DESIGN_MODIFICATION_DETAIL]);
          useFormMethods.reset(useFormMethods.getValues());

          if (isSuccessCreate) {
            useFormMethods.reset({}, { keepDirty: false });

            queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.DESIGN_MODIFICATION_BY_T1]);
            setTimeout(() => {
              navigate(routes.t3.view.path, { id: createResult?.item?.id?.toString() });
            }, 0);
            return;
          }
          if (isSuccessCreateRevision) {
            useFormMethods.reset({}, { keepDirty: false });

            queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.DESIGN_MODIFICATION_BY_T1]);
            setTimeout(() => {
              navigate(routes.t3.view.path, { id: dataCreateRevision?.item?.id?.toString() });
            }, 0);
          }
        }}
      />
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: errorCreate, genericError: 'Issue Design Modification Failed', reset: resetCreate },
          { error: errorUpdate, genericError: 'Issue Design Modification Failed', reset: resetUpdate },
          { error: errorApprove, genericError: 'Approve Design Modification Failed', reset: resetApprove },
          {
            error: errorApproveAndCheckedAsLambeth,
            genericError: 'Approve Design Modification Failed',
            reset: resetApproveAndCheckedAsLambeth,
          },
          {
            error: errorRequestEndorsement,
            genericError: 'Request Endorsement Failed',
            reset: resetRequestEndorsement,
          },
          { error: errorCancel, genericError: 'Cancel Design Modification Failed', reset: resetCancel },
          { error: errorCreateRevision, genericError: 'Create Revision Failed', reset: resetCreateRevision },
        ]}
      />
      <DetailPageTopBar leftItems={leftButtonBarItems} isReadOnly={isReadOnly} />
      <NoteList items={notes} />

      <DesignModificationDetailTabForm
        isReadOnly={isReadOnly}
        formFieldMethods={formFieldMethods}
        mode={mode}
        designModificationId={id}
        designModification={designModification}
        masterSchedule={masterSchedule}
        masterScheduleId={masterScheduleId}
        checkerCompanyOptions={checkerCompanyOptions}
        checkerTeamOptions={checkerTeamOptions}
        designerCompanyOptions={designerCompanyOptions}
        designerTeamOptions={designerTeamOptions}
        searchParamIssueRole={searchParamIssueRole}
        isSuperseded={isSuperseded}
      />
      <DesignModificationDetailTabSignature
        formFieldMethods={formFieldMethods}
        mode={mode}
        isReadOnly={isReadOnly}
        mutationApprove={mutationApprove}
        mutationCreate={mutationCreate}
        mutationApproveAndCheckedAsLambeth={mutationApproveAndCheckedAsLambeth}
        masterSchedule={masterSchedule}
        onClickApprove={openConfirmationModalWithType}
        topBtnsVisibility={topBtnsVisibility}
        designModification={designModification}
        designModificationId={id}
        confirmationActionType={confirmationActionType}
        isSuperseded={isSuperseded}
        isAnyActionLoading={isAnyActionLoading}
      />

      {renderConfirmationPortal(
        isLoadingCreate || isLoadingUpdate || isLoadingApprove || isLoadingCancel || isLoadingRequestEndorsement,
        isErrorCreate || isErrorUpdate || isErrorApprove || isErrorCancel || isErrorRequestEndorsement,
        isSuccessCreate || isSuccessUpdate || isSuccessApprove || isSuccessRequestEndorsement || isSuccessCancel,
      )}
      {renderReassignCheckerModal({ projectId, designModification })}
    </div>
  );
};
